import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AuthPageShell from '../components/auth/AuthPageShell';
import {
  acceptLegacyGatheringInvitation,
  acceptGatheringInvitation,
  fetchLegacyGatheringInvitation,
  fetchGatheringInvitation,
} from '../api/invitations';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import {
  formatDisplayDate,
  formatDisplayTime,
  timeInputValueFromDateTime,
} from '../i18n/formatters';
import { useToast } from '../components/tavern/ToastProvider';

const GatheringInvitePage = ({ legacy = false }) => {
  const { nightId, token } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const language = i18n.resolvedLanguage || i18n.language;
  const inviteKey = legacy ? nightId : token;
  const returnTo = legacy
    ? `/invite/gathering/night/${encodeURIComponent(nightId || '')}`
    : `/invite/gathering/${encodeURIComponent(token || '')}`;
  const loginPath = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  const registerPath = `/register?returnTo=${encodeURIComponent(returnTo)}`;
  const scheduledDate = invitation?.scheduledAt
    ? formatDisplayDate(invitation.scheduledAt, language)
    : '';
  const scheduledTime = invitation?.scheduledAt
    ? formatDisplayTime(timeInputValueFromDateTime(invitation.scheduledAt), language)
    : '';
  const subtitle = useMemo(() => {
    if (!invitation) return t('gatheringInvite.subtitle');

    return t('gatheringInvite.subtitleWithInviter', {
      inviterName: invitation.inviterName,
    });
  }, [invitation, t]);

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setErrorKey('');

    const loadInvitation = legacy
      ? fetchLegacyGatheringInvitation(inviteKey)
      : fetchGatheringInvitation(inviteKey);

    loadInvitation
      .then((data) => {
        if (!mounted) return;
        setInvitation(data.invitation || null);
      })
      .catch((error) => {
        if (!mounted) return;
        setErrorKey(
          Number(error?.response?.status) === 410
            ? 'gatheringInvite.expired'
            : 'gatheringInvite.notFound'
        );
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [inviteKey, legacy]);

  const handleAccept = async () => {
    if (!inviteKey || accepting) return;

    setAccepting(true);
    setErrorKey('');

    try {
      const response = legacy
        ? await acceptLegacyGatheringInvitation(inviteKey)
        : await acceptGatheringInvitation(inviteKey);
      toast.success(t('gatheringInvite.joined'));
      navigate(`/nights?focusNightId=${response.nightId}`, { replace: true });
    } catch (error) {
      setErrorKey(
        Number(error?.response?.status) === 410
          ? 'gatheringInvite.expired'
          : 'gatheringInvite.acceptError'
      );
    } finally {
      setAccepting(false);
    }
  };

  return (
    <AuthPageShell
      compact
      title={t('gatheringInvite.title')}
      subtitle={subtitle}
    >
      <Stack spacing={2}>
        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={20} />
            <Typography color="text.secondary">{t('gatheringInvite.loading')}</Typography>
          </Stack>
        ) : null}

        {errorKey ? <Alert severity="warning">{t(errorKey)}</Alert> : null}

        {!loading && invitation ? (
          <Stack spacing={1}>
            <Typography variant="h3">{invitation.title}</Typography>
            <Typography color="text.secondary">
              {t('gatheringInvite.scheduledFor', {
                date: scheduledDate,
                time: scheduledTime,
              })}
            </Typography>
          </Stack>
        ) : null}

        {!loading && invitation && isAuthenticated ? (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<TableRestaurantIcon />}
            disabled={accepting}
            onClick={handleAccept}
            fullWidth
          >
            {accepting ? t('gatheringInvite.joining') : t('gatheringInvite.joinTable')}
          </Button>
        ) : null}

        {!loading && invitation && !isAuthenticated ? (
          <Stack spacing={1.25}>
            <Button
              component={RouterLink}
              to={registerPath}
              variant="contained"
              color="secondary"
              startIcon={<PersonAddAltIcon />}
              fullWidth
            >
              {t('gatheringInvite.createAccount')}
            </Button>
            <Button
              component={RouterLink}
              to={loginPath}
              variant="outlined"
              startIcon={<LoginIcon />}
              fullWidth
            >
              {t('gatheringInvite.signIn')}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </AuthPageShell>
  );
};

export default GatheringInvitePage;
